import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, filter, Subscription, take } from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { Representative } from 'src/app/shared/models/dtos/register-form/representative/representative';
import { getTranslocoLang } from 'src/app/utils/translocoLang';

@Component({
  selector: 'app-edit-settings',
  templateUrl: './edit-settings.component.html',
  styleUrls: ['./edit-settings.component.scss'],
})

/**
 * Edit settings form component
 */
export class EditSettingsComponent implements OnInit, OnDestroy {
  representative$: BehaviorSubject<Representative | undefined>;
  loading: boolean = false;
  editLanguageFormGroup = new FormGroup({
    language: new FormControl<string | null>(null),
  });
  private readonly updateSuccessful = 'success-updating-data';
  labels = {
    changeSystemLanguage: 'change-system-language',
    english: 'english',
    portuguese: 'portuguese',
    spanish: 'spanish',
    saveSettingsChanges: 'save-settings-changes',
    updateSuccessful: this.updateSuccessful,
    updateFailed: 'error-updating-data',
  };
  private readonly subsink = new Subscription();

  /**
   * @costructor
   */
  constructor(
    private translationService: TranslationService,
    private userService: UserService,
    readonly authenticatedUserService: AuthenticatedUserService,
    private snackBarService: SnackBarService
  ) {
    this.representative$ = authenticatedUserService.representative$;
  }

  /**
   * on init
   */
  ngOnInit(): void {
    this.subsink.add(this.translationService.translateObject$(this.labels));
    this.subsink.add(
      this.representative$.subscribe((info) => {
        if (info?.language) {
          this.editLanguageFormGroup?.controls['language'].setValue(
            info.language!
          );
        } else {
          this.editLanguageFormGroup?.controls['language'].setValue('en');
        }
      })
    );
  }

  /**
   * cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Returns language FormControl from FormGroup
   * @return {FormControl}
   */
  get languageControl() {
    return this.editLanguageFormGroup.controls['language'];
  }

  /**
   * Saves settings changes
   */
  saveSettingsChanges() {
    this.loading = true;
    const obj = {
      language: this.languageControl.value,
    };

    this.userService
      .updateRepresentativeLanguage(obj)
      .pipe(
        filter((res) => res != null),
        take(1)
      )
      .subscribe({
        next: (representative) => {
          this.loading = false;
          const translocoLang = getTranslocoLang(representative.language!);
          this.translationService.setActiveLang(translocoLang!);
          this.authenticatedUserService.representative = representative;
          this.authenticatedUserService.resetBannerData();
          this.translationService
            .translateString(this.updateSuccessful)
            .then((translation) => {
              this.snackBarService.success(translation);
            });
        },
        error: () => {
          this.snackBarService.error(this.labels.updateFailed);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  /**
   * The form must be disabled if there's no changes in the data
   * @return {boolean}
   */
  isFormDisabled() {
    return (
      this.editLanguageFormGroup.get('language')?.value ===
      this.representative$.value?.language
    );
  }
}
